import { useEffect, useMemo, useState } from 'react'
import { getRoundProgress } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

// MF-399 — Cohort View: % de alumnos marcados por skill, comparando rounds
// (Round 1 vs Round 2 ...). Pivotea las filas planas de /assessment/round-progress
// a una tabla por assessment: filas = pregunta, columnas = cada round.
const fmtDate = (d) => {
  if (!d) return null
  const parsed = new Date(d)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
}

const groupByAssessment = (rows = []) => {
  const byAssessment = new Map()

  for (const r of rows) {
    const aKey = r.assessment_id || r.assessment_name
    if (!byAssessment.has(aKey)) {
      byAssessment.set(aKey, {
        assessmentName: r.assessment_name || r.assessment_id,
        rounds: new Map(), // round_label -> { order, sittingDate, dueDate }
        questions: new Map() // question_title -> { round_label -> {marked, students, pct} }
      })
    }
    const group = byAssessment.get(aKey)
    if (!group.rounds.has(r.round_label)) {
      group.rounds.set(r.round_label, {
        order: r.round_order ?? 0,
        sittingDate: r.sitting_date ?? null,
        dueDate: r.due_date ?? null
      })
    }
    if (!group.questions.has(r.question_title)) group.questions.set(r.question_title, {})
    group.questions.get(r.question_title)[r.round_label] = {
      marked: r.marked ?? 0,
      students: r.students ?? 0,
      pct: r.marked_pct ?? 0
    }
  }

  return [...byAssessment.values()].map((g) => ({
    assessmentName: g.assessmentName,
    rounds: [...g.rounds.entries()]
      .sort((a, b) => a[1].order - b[1].order)
      .map(([label, meta]) => ({ label, sittingDate: meta.sittingDate, dueDate: meta.dueDate })),
    questions: [...g.questions.entries()].map(([title, cells]) => ({ title, cells }))
  }))
}

const pctColor = (pct) => {
  if (pct >= 80) return '#2e7d32'
  if (pct >= 40) return '#f39c12'
  return '#c0392b'
}

const Cell = ({ cell }) => {
  if (!cell) return <span style={{ color: '#c4c4c4' }}>—</span>
  return (
    <div title={`${cell.marked} / ${cell.students} marked`}>
      <div style={{ fontWeight: 600, color: pctColor(cell.pct), fontVariantNumeric: 'tabular-nums' }}>
        {cell.pct}%
      </div>
      <div
        style={{
          height: 5,
          borderRadius: 3,
          background: '#eef0f2',
          marginTop: 4,
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, cell.pct)}%`,
            background: pctColor(cell.pct),
            transition: 'width 200ms ease'
          }}
        />
      </div>
    </div>
  )
}

const thBase = {
  textAlign: 'left',
  padding: '10px 16px',
  borderBottom: '1px solid #e6e9ed',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: '#7a8796',
  whiteSpace: 'nowrap'
}

const AssessmentTable = ({ group }) => (
  <div
    style={{
      marginBottom: 20,
      border: '1px solid #e6e9ed',
      borderRadius: 8,
      background: '#fff',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      overflow: 'hidden'
    }}
  >
    <h3
      style={{
        margin: 0,
        padding: '14px 16px',
        fontSize: 14,
        fontWeight: 600,
        color: '#2b5784',
        borderBottom: '1px solid #eef0f2'
      }}
    >
      {group.assessmentName}
    </h3>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...thBase, minWidth: 220 }}>Skill / Question</th>
            {group.rounds.map((round) => {
              const sitting = fmtDate(round.sittingDate)
              const due = fmtDate(round.dueDate)
              return (
                <th key={round.label} style={{ ...thBase, minWidth: 110 }}>
                  <div>{round.label}</div>
                  {(sitting || due) && (
                    <div
                      style={{
                        fontWeight: 400,
                        fontSize: 10,
                        letterSpacing: 0,
                        textTransform: 'none',
                        color: '#98a2b3',
                        marginTop: 3
                      }}
                    >
                      {sitting && <div>Sitting: {sitting}</div>}
                      {due && <div>Due: {due}</div>}
                    </div>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {group.questions.map((q, i) => (
            <tr key={q.title} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
              <td
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #f2f4f6',
                  color: '#34495e',
                  fontWeight: 500
                }}
              >
                {q.title}
              </td>
              {group.rounds.map((round) => (
                <td key={round.label} style={{ padding: '10px 16px', borderBottom: '1px solid #f2f4f6' }}>
                  <Cell cell={q.cells[round.label]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

const LegendDot = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
    {label}
  </span>
)

const RoundProgress = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [finalizedOnly, setFinalizedOnly] = useState(false)

  useEffect(() => {
    setLoading(true)
    getRoundProgress({ finalizedOnly })
      .then((res) => setRows(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading round progress'))
      .finally(() => setLoading(false))
  }, [finalizedOnly])

  const groups = useMemo(() => groupByAssessment(rows), [rows])

  return (
    <div style={{ padding: 24, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#2b5784' }}>
            Round Progress (Cohort)
          </h2>
          <span style={{ fontSize: 12, color: '#98a2b3' }}>% of students marked, per skill and round</span>
          <DataAsOf />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#7a8796' }}>
            <LegendDot color='#2e7d32' label='≥ 80%' />
            <LegendDot color='#f39c12' label='40–79%' />
            <LegendDot color='#c0392b' label='< 40%' />
          </div>
          <label
            style={{
              fontSize: 12,
              color: '#475467',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer'
            }}
          >
            <input
              type='checkbox'
              checked={finalizedOnly}
              onChange={(e) => setFinalizedOnly(e.target.checked)}
            />
            Finalized rounds only
          </label>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !groups.length ? (
        <p style={{ color: '#888' }}>No round data available.</p>
      ) : (
        groups.map((g) => <AssessmentTable key={g.assessmentName} group={g} />)
      )}
    </div>
  )
}

export default RoundProgress
